import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Mail,
  Lock,
  AlertCircle,
  X,
  CheckCircle,
  GraduationCap,
  Users,
  Building2,
  Eye,       // Password Toggle Icon
  EyeOff,    // Password Toggle Icon
  Clock      // Clock icon for rate limit
} from 'lucide-react';

// Make sure your api.js login function accepts query params or update it below
import { login, resendVerificationEmail } from '../../../services/api';
import { formFillApi } from '../../../services/formFillApi';
import { useTheme } from '../../Home/ThemeContext';

// IMPORT THE MODAL YOU WROTE
import TermsModal from './TermsModal'; 

// USER TYPES CONFIG
const userTypes = [
  {
    type: 'student',
    label: 'Student',
    icon: GraduationCap,
    description: 'Access courses and learning materials',
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    type: 'partner',
    label: 'Partner',
    icon: Users,
    description: 'Business partners & counselors',
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    type: 'college',
    label: 'College',
    icon: Building2,
    description: 'Administrative access for institutions',
    gradient: 'from-orange-500 to-red-500'
  }
];

export default function LoginPage() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [userType, setUserType] = useState('student');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  
  // ERROR STATES
  const [error, setError] = useState('');
  const [isRateLimited, setIsRateLimited] = useState(false); // New state for 429
  const [rateLimitMsg, setRateLimitMsg] = useState('');      // New state for 429 message

  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendSuccess, setResendSuccess] = useState('');

  // NEW STATES
  const [showPassword, setShowPassword] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Detect ?type=partner or ?type=college
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');

    if (type === 'counselor') setUserType('partner');
    else if (['student', 'partner', 'college'].includes(type)) setUserType(type);
  }, [location]);

  // INPUT CHANGE
  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setResendSuccess('');
    setIsRateLimited(false); // Clear rate limit warning on typing
  };

  // 🟢 FIXED: Resend Verification Logic
  const handleResendVerification = async () => {
    // Fallback to formData.email if the specific state is empty
    const targetEmail = verificationEmail || formData.email;

    if (!targetEmail) {
      setError('Please enter your email address to resend verification.');
      return;
    }

    setIsLoading(true);
    setError('');
    setResendSuccess('');

    try {
      // ⚠️ CRITICAL FIX: Pass an object { email: ... } 
      // Your Go backend requires a JSON body with an "email" field.
      await resendVerificationEmail({ email: targetEmail });
      
      setResendSuccess(`Verification email sent to ${targetEmail}. Please check your inbox.`);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.response?.data?.error;

      if (status === 429) {
        setError("Too many attempts. Please wait 1 hour before resending.");
      } else if (status === 409) {
        setError("This email is already verified. Please log in.");
        setNeedsVerification(false); // Hide the button since they are verified
      } else {
        setError(msg || 'Failed to resend verification email.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // MAIN LOGIN SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password)
      return setError('Please enter both email and password');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email))
      return setError('Please enter a valid email');

    setIsLoading(true);
    setError('');
    setIsRateLimited(false);
    setNeedsVerification(false);
    setResendSuccess('');

    try {
      const response = await login(formData.email, formData.password, userType); 

      const { userType: returnedType, user, token } = response.data;
      const businessType = user?.businessType || user?.business_type;
      const userId = user?.id || user?._id;
      const registrationStatus = user?.registrationStatus || user?.registration_status;

      // STORE USER AUTH
      localStorage.setItem('token', token);
      localStorage.setItem('auth_token', token);
      localStorage.setItem('userType', returnedType);
      localStorage.setItem('userId', userId);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('user_data', JSON.stringify(user));

      setCookie('auth_token', token, 7);
      setCookie('user_id', userId, 7);
      setCookie('user_data', JSON.stringify(user), 7);

      // REDIRECT RULES
      let finalType = returnedType;
      let finalRedirect = '/';

      if (returnedType === 'college') {
        finalType = 'college';
        finalRedirect = '/college/dashboard';
      }
      else if (returnedType === 'partner') {
        // Determine specific dashboard based on business type
        finalType = businessType === 'counselor' ? 'counselor'
                 : businessType === 'agent' ? 'partner' // agents share partner dash or have their own
                 : businessType === 'institution' ? 'partner'
                 : 'partner';

        if (finalType === 'counselor') {
          if (
            registrationStatus === 'incomplete' || 
            registrationStatus === 'pending_payment' || 
            !registrationStatus
          ) {
            console.log("⚠️ Counselor registration incomplete. Redirecting to form.");
            finalRedirect = '/counselor/register'; 
          } else {
            finalRedirect = '/counselor/dashboard';
          }
        } else {
          // If you have specific dashboards for agents/institutions
          finalRedirect = 
            businessType === 'agent' ? '/agent/dashboard' :
            businessType === 'institution' ? '/institution/dashboard' :
            '/partner/dashboard';
        }
      }
      else if (returnedType === 'student') {
        finalType = 'student';
        try {
          // Small delay to ensure token is set before fetching applications
          await new Promise(res => setTimeout(res, 200)); 
          const apiResponse = await formFillApi.getMyApplications();
          let apps = [];
          if (Array.isArray(apiResponse)) apps = apiResponse;
          else if (apiResponse?.data) apps = apiResponse.data;
          else if (apiResponse?.applications) apps = apiResponse.applications;

          finalRedirect = apps.length > 0 ? '/student/dashboard' : '/allcollege';
        } catch {
          finalRedirect = '/student/dashboard';
        }
      }
      else {
        finalRedirect = getDashboardLink(returnedType);
      }

      localStorage.setItem('userType', finalType);
      localStorage.setItem('user_type', finalType);
      setCookie('user_type', finalType, 7);

      setTimeout(() => {
        window.location.href = finalRedirect;
      }, 100);

    } catch (err) {
      // 🔴 HANDLE ERRORS
      const status = err.response?.status;
      const data = err.response?.data;
      const msg = data?.message || data?.error;

      // 1. Not Verified (403)
      if (status === 403 && msg?.toLowerCase().includes("verify your email")) {
        setError('Your email is not verified yet.');
        setNeedsVerification(true);
        setVerificationEmail(formData.email);
      }
      // 2. Rate Limit (429)
      else if (status === 429) {
        setIsRateLimited(true);
        setRateLimitMsg(msg || "Too many attempts. Please try again later.");
      } 
      // 3. Unauthorized (401)
      else if (status === 401) {
        setError(msg || 'Invalid email or password.');
      } 
      else {
        setError(msg || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentUserType = userTypes.find(t => t.type === userType);

  return (
    <div className={`min-h-screen pt-32 pb-12 px-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-md mx-auto">

        {/* HEADER */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r ${currentUserType.gradient} text-white mb-4`}>
            <currentUserType.icon className="w-6 h-6" />
            <span className="font-bold">{currentUserType.label} Login</span>
          </div>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
            {currentUserType.description}
          </p>
        </div>

        {/* CARD */}
        <div className={`rounded-2xl shadow-2xl p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>

          {/* 🔴 RATE LIMIT ERROR (429) */}
          {isRateLimited && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 flex flex-col gap-2">
              <div className="flex items-center gap-2 font-semibold">
                <Clock className="w-5 h-5" />
                <span>Login Blocked</span>
              </div>
              <p className="text-sm">
                {rateLimitMsg}
              </p>
              <p className="text-xs text-red-500 mt-1">
                Contact <a href="mailto:info@vidyazon.com" className="underline font-bold">info@vidyazon.com</a> for instant unlock.
              </p>
            </div>
          )}

          {/* 🟠 STANDARD ERROR / VERIFICATION */}
          {error && !isRateLimited && (
            <div className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${
              needsVerification ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-600'
                                : 'bg-red-500/10 border-red-500/20 text-red-500'
            }`}>
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm">{error}</p>

                {needsVerification && (
                  <div className="mt-2">
                    <p className="text-xs mb-1 opacity-80">Did you miss the email?</p>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      className="text-sm underline font-bold hover:opacity-80 transition-opacity"
                    >
                      Resend Verification Email
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => setError('')}>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* 🟢 SUCCESS */}
          {resendSuccess && (
            <div className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-600 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm flex-1">{resendSuccess}</p>
              <button onClick={() => setResendSuccess('')}>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* LOGIN FORM */}
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* EMAIL */}
            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Email Address <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isRateLimited} // Disable input if blocked
                  className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white'
                               : 'bg-white border-gray-300 text-gray-900'
                  } ${isRateLimited ? 'opacity-50 cursor-not-allowed' : ''}`}
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            {/* PASSWORD WITH TOGGLE */}
            <div>
              <div className="flex justify-between mb-2">
                <label className={`block text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Password *</label>
                <a href="/forgot-password" className="text-blue-600 text-sm">Forgot Password?</a>
              </div>

              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={isRateLimited} // Disable input if blocked
                  className={`w-full pl-11 pr-12 py-3.5 rounded-lg border-2 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white'
                               : 'bg-white border-gray-300 text-gray-900'
                  } ${isRateLimited ? 'opacity-50 cursor-not-allowed' : ''}`}
                  placeholder="Enter your password"
                />
                
                {/* TOGGLE BUTTON */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isRateLimited}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors ${
                    isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* LOGIN BUTTON */}
            <button
              type="submit"
              disabled={isLoading || isRateLimited}
              className={`w-full bg-gradient-to-r ${currentUserType.gradient} text-white font-bold py-4 rounded-lg flex justify-center gap-2 ${
                (isLoading || isRateLimited) ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90 transform hover:-translate-y-0.5 transition-all'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Logging in...
                </>
              ) : isRateLimited ? (
                <>Login Temporarily Disabled</>
              ) : (
                <>Login to {currentUserType.label} Portal</>
              )}
            </button>

            {/* TERMS LINK */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className={`text-xs hover:underline ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
              >
                By logging in, you agree to our Terms & Conditions
              </button>
            </div>

          </form>

          {/* SIGNUP */}
          <p className={`text-center mt-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Don't have an account?  
            <a href={`/signup?type=${userType}`} className="text-blue-600 ml-1 font-semibold">Sign up</a>
          </p>

          {/* SWITCH USER TYPE */}
          <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`text-sm mb-3 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Login as different user type:
            </p>

            <div className="flex gap-2 justify-center flex-wrap">
              {userTypes.map(t => (
                <button
                  key={t.type}
                  onClick={() => {
                    navigate(`/login${t.type !== 'student' ? '?type=' + t.type : ''}`);
                    setError('');
                    setIsRateLimited(false);
                    setNeedsVerification(false);
                    setResendSuccess('');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    userType === t.type
                      ? `bg-gradient-to-r ${t.gradient} text-white shadow-md`
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* RENDER IMPORTED MODAL */}
      <TermsModal 
        isOpen={showTerms} 
        onClose={() => setShowTerms(false)} 
        isDarkMode={isDarkMode} 
      />

    </div>
  );
}

// HELPER FUNCTIONS
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';')[0];
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Strict`;
}

function getDashboardLink(type) {
  switch (type) {
    case 'student': return '/student/dashboard';
    case 'partner': return '/partner/dashboard';
    case 'college': return '/college/dashboard';
    case 'counselor': return '/counselor/dashboard';
    default: return '/dashboard';
  }
}